import React, { useEffect, useState } from 'react'
import moment from 'moment'
import Autocomplete from '@mui/material/Autocomplete'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import Chip from '@mui/material/Chip'
import Collapse from '@mui/material/Collapse'
import Divider from '@mui/material/Divider'
import Grid from '@mui/material/Grid'
import InputAdornment from '@mui/material/InputAdornment'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import SearchIcon from '@mui/icons-material/Search'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import TuneIcon from '@mui/icons-material/Tune'
import AnDateField from '../../../components/DateTime/DateField'
import API from '../../../services/API'
import PCOptions from '../ePCR/History/presentingComplaintOptions'
import EmployeeSelector from '../../events/EmployeeSelector'

export const FORM_TYPES = [
  'ePCR',
  'Fast Track',
  'Welfare',
  'Critical Care',
  'Paper',
]

const DATE_PRESETS = [
  {
    label: 'Today',
    range: () => [moment().startOf('day'), moment().endOf('day')],
  },
  {
    label: 'Past 7 Days',
    range: () => [
      moment().subtract(7, 'days').startOf('day'),
      moment().endOf('day'),
    ],
  },
  {
    label: 'Past 30 Days',
    range: () => [
      moment().subtract(30, 'days').startOf('day'),
      moment().endOf('day'),
    ],
  },
  {
    label: 'This Month',
    range: () => [moment().startOf('month'), moment().endOf('month')],
  },
  {
    label: 'All Dates',
    range: () => [null, null],
  },
]

const presentingComplaintOptions = Object.keys(PCOptions).reduce(
  (accumulator, category) => {
    PCOptions[category]
      .slice() // don't mutate source
      .sort((a, b) => {
        const aLabel = typeof a === 'string' ? a : a.label
        const bLabel = typeof b === 'string' ? b : b.label
        return aLabel.localeCompare(bLabel)
      })
      .forEach((item) => {
        if (typeof item === 'string') {
          accumulator.push({ category, item, keywords: [] })
        } else {
          accumulator.push({
            category,
            item: item.label,
            keywords: item.keywords || [],
          })
        }
      })
    return accumulator
  },
  [],
)

export const FilterGroup = ({ label, children }) => (
  <Stack gap={0.75}>
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}
    >
      {label}
    </Typography>
    <Stack direction="row" gap={1} flexWrap="wrap">
      {children}
    </Stack>
  </Stack>
)

export const ToggleChip = ({ label, selected, color = 'anMainBlue', onClick }) => (
  <Chip
    label={label}
    size="small"
    clickable
    color={selected ? color : 'default'}
    variant={selected ? 'filled' : 'outlined'}
    onClick={onClick}
    sx={{ fontWeight: 600, borderRadius: 1 }}
  />
)

const SearchBar = ({
  searchParams,
  setSearchParams,
  performSearch,
  loadingSearch,
}) => {
  const [eventOptions, setEventOptions] = useState([])
  const [advancedSearch, setAdvancedSearch] = useState(false)

  const toggleListValue = (field, value) =>
    setSearchParams((prev) => {
      const current = prev?.[field] ?? []
      return {
        ...prev,
        [field]: current.includes(value)
          ? current.filter((x) => x !== value)
          : [...current, value],
      }
    })

  const advancedFilterCount = [
    searchParams?.lastName,
    searchParams?.firstName,
    searchParams?.dob,
    searchParams?.completedBy,
    searchParams?.presentingComplaint?.length,
    searchParams?.hsConcern,
    searchParams?.sgConcerns,
    (searchParams?.status ?? []).length !== 2,
    (searchParams?.types ?? []).length !== FORM_TYPES.length,
  ].filter(Boolean).length

  const resetAdvanced = () =>
    setSearchParams((prev) => ({
      ...prev,
      status: ['Draft', 'Submitted'],
      types: [...FORM_TYPES],
      completedBy: null,
      firstName: null,
      lastName: null,
      dob: null,
      presentingComplaint: [],
      hsConcern: false,
      sgConcerns: null,
    }))

  useEffect(() => {
    if (
      moment(searchParams?.startDate).isValid() &&
      moment(searchParams?.endDate).isValid()
    ) {
      API.post('/epcr/search/eventoptions', {
        startDate: moment(searchParams.startDate)
          .startOf('day')
          .toLocaleString(),
        endDate: moment(searchParams.endDate).endOf('day').toLocaleString(),
      }).then((res) => res.status === 200 && setEventOptions(res.data))
    }
  }, [searchParams?.startDate, searchParams?.endDate])

  const applyPreset = (range) => {
    const [startDate, endDate] = range()
    setSearchParams((prev) => ({ ...prev, startDate, endDate }))
    performSearch({ startDate, endDate })
  }

  const activePreset = DATE_PRESETS.find(({ range }) => {
    const [presetStart, presetEnd] = range()
    if (!presetStart && !presetEnd)
      return !searchParams?.startDate && !searchParams?.endDate
    return (
      searchParams?.startDate &&
      searchParams?.endDate &&
      presetStart.isSame(moment(searchParams.startDate), 'day') &&
      presetEnd.isSame(moment(searchParams.endDate), 'day')
    )
  })?.label

  return (
    <Card
      variant="outlined"
      component="form"
      onSubmit={(e) => {
        e.preventDefault()
        performSearch()
      }}
      sx={{ p: 1.5 }}
    >
      <Stack gap={1.5}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          gap={1.5}
          alignItems={{ md: 'center' }}
        >
          <TextField
            placeholder="ePCR number…"
            size="small"
            value={searchParams?.epcrNumber ?? ''}
            onChange={(e) =>
              setSearchParams((prev) => ({
                ...prev,
                epcrNumber: e.target.value,
              }))
            }
            sx={{ flex: 1, minWidth: 180 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <Stack direction="row" gap={1.5} alignItems="center">
            <AnDateField
              fullWidth
              date={searchParams.startDate}
              size="small"
              onChange={(newDate) =>
                setSearchParams((prev) => ({ ...prev, startDate: newDate }))
              }
              label="From"
              clearable
            />
            <AnDateField
              date={searchParams.endDate}
              size="small"
              clearable
              fullWidth
              onChange={(newDate) =>
                setSearchParams((prev) => ({ ...prev, endDate: newDate }))
              }
              label="To"
            />
          </Stack>
          <Autocomplete
            options={eventOptions}
            getOptionLabel={(option) =>
              `${option.title} (${moment(option.startDate).format(
                'Do MMM YYYY',
              )})`
            }
            renderInput={(params) => (
              <TextField {...params} label="Event" size="small" fullWidth />
            )}
            onChange={(e, newValue) =>
              setSearchParams((prev) => ({ ...prev, event: newValue?._id }))
            }
            getOptionKey={(option) => option._id}
            isOptionEqualToValue={(option, value) => option._id === value._id}
            noOptionsText="No events found for these dates."
            sx={{ flex: 1, minWidth: 220 }}
          />
        </Stack>

        <Stack
          direction="row"
          gap={1}
          flexWrap="wrap"
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack direction="row" gap={1} flexWrap="wrap">
            {DATE_PRESETS.map(({ label, range }) => (
              <Chip
                key={label}
                label={label}
                size="small"
                color={activePreset === label ? 'anMainBlue' : 'default'}
                variant={activePreset === label ? 'filled' : 'outlined'}
                onClick={() => applyPreset(range)}
                sx={{ fontWeight: 600 }}
              />
            ))}
          </Stack>
          <Stack direction="row" gap={1}>
            <Button
              size="small"
              color="anMainBlue"
              variant={advancedSearch ? 'contained' : 'outlined'}
              startIcon={<TuneIcon />}
              onClick={() => setAdvancedSearch((prev) => !prev)}
            >
              Advanced
              {advancedFilterCount > 0 && (
                <Chip
                  label={advancedFilterCount}
                  size="small"
                  color={advancedSearch ? 'anPurple' : 'anMainBlue'}
                  sx={{ ml: 1, height: 18, fontWeight: 700, borderRadius: 1 }}
                />
              )}
            </Button>
            <Button
              variant="contained"
              color="success"
              size="small"
              disabled={loadingSearch}
              endIcon={<SearchIcon />}
              type="submit"
            >
              Search
            </Button>
          </Stack>
        </Stack>

        <Collapse in={advancedSearch}>
          <Stack gap={2}>
            <Divider />

            <Stack
              direction={{ xs: 'column', lg: 'row' }}
              gap={{ xs: 2, lg: 4 }}
              flexWrap="wrap"
            >
              <FilterGroup label="Status">
                <ToggleChip
                  label="Draft"
                  color="error"
                  selected={(searchParams?.status ?? []).includes('Draft')}
                  onClick={() => toggleListValue('status', 'Draft')}
                />
                <ToggleChip
                  label="Submitted"
                  color="success"
                  selected={(searchParams?.status ?? []).includes('Submitted')}
                  onClick={() => toggleListValue('status', 'Submitted')}
                />
              </FilterGroup>

              <FilterGroup label="Form Types">
                {FORM_TYPES.map((type) => (
                  <ToggleChip
                    key={type}
                    label={type}
                    selected={(searchParams?.types ?? []).includes(type)}
                    onClick={() => toggleListValue('types', type)}
                  />
                ))}
              </FilterGroup>

              <FilterGroup label="Safeguarding Concerns">
                {['Any', 'New', 'Completed'].map((option) => (
                  <ToggleChip
                    key={option}
                    label={option}
                    color="warning"
                    selected={searchParams?.sgConcerns === option}
                    onClick={() =>
                      setSearchParams((prev) => ({
                        ...prev,
                        sgConcerns:
                          prev?.sgConcerns === option ? null : option,
                      }))
                    }
                  />
                ))}
              </FilterGroup>

              <FilterGroup label="Flags">
                <ToggleChip
                  label="H&S Concern"
                  color="warning"
                  selected={!!searchParams?.hsConcern}
                  onClick={() =>
                    setSearchParams((prev) => ({
                      ...prev,
                      hsConcern: !prev?.hsConcern,
                    }))
                  }
                />
              </FilterGroup>
            </Stack>

            <Grid container rowSpacing={2} columnSpacing={1.5}>
              <Grid item xs={12} md={6} lg={3}>
                <TextField
                  label="Patient Surname"
                  value={searchParams?.lastName ?? ''}
                  onChange={(e) =>
                    setSearchParams((prev) => ({
                      ...prev,
                      lastName: e.target.value,
                    }))
                  }
                  size="small"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6} lg={3}>
                <TextField
                  label="Patient First Name"
                  value={searchParams?.firstName ?? ''}
                  onChange={(e) =>
                    setSearchParams((prev) => ({
                      ...prev,
                      firstName: e.target.value,
                    }))
                  }
                  size="small"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6} lg={2}>
                <AnDateField
                  date={searchParams.dob}
                  size="small"
                  clearable
                  fullWidth
                  onChange={(newDate) =>
                    setSearchParams((prev) => ({ ...prev, dob: newDate }))
                  }
                  label="Date of Birth"
                />
              </Grid>
              <Grid item xs={12} md={6} lg={4}>
                <EmployeeSelector
                  value={searchParams?.completedBy ?? null}
                  onChange={(e, newValue) =>
                    setSearchParams((prev) => ({
                      ...prev,
                      completedBy: newValue,
                    }))
                  }
                  label="Completed By"
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={12} lg={8}>
                <Autocomplete
                  options={presentingComplaintOptions}
                  getOptionLabel={(option) => option.item || option || ''}
                  groupBy={(option) => option.category}
                  multiple
                  clearOnBlur
                  value={searchParams?.presentingComplaint ?? []}
                  isOptionEqualToValue={(option, value) =>
                    option.item === value
                  }
                  onChange={(e, value) =>
                    setSearchParams((prev) => ({
                      ...prev,
                      presentingComplaint: value.map((x) => x?.item ?? x),
                    }))
                  }
                  renderTags={(tags, getTagProps) => (
                    <Stack direction="row" flexWrap="wrap" gap={0.5}>
                      {tags.map((tag, index) => {
                        const { key, ...chipProps } = getTagProps({ index })
                        return (
                          <Chip
                            key={key}
                            label={tag}
                            size="small"
                            sx={{ borderRadius: 1 }}
                            {...chipProps}
                          />
                        )
                      })}
                    </Stack>
                  )}
                  renderInput={(params) => (
                    <TextField {...params} label="Presenting Complaint" />
                  )}
                  size="small"
                />
              </Grid>
              <Grid
                item
                xs={12}
                lg={4}
                display="flex"
                alignItems="center"
                justifyContent="flex-end"
              >
                <Button
                  size="small"
                  color="inherit"
                  startIcon={<RestartAltIcon />}
                  onClick={resetAdvanced}
                  disabled={advancedFilterCount === 0}
                  sx={{ color: 'text.secondary' }}
                >
                  Reset Filters
                </Button>
              </Grid>
            </Grid>
          </Stack>
        </Collapse>
      </Stack>
    </Card>
  )
}

export default SearchBar
